import { useDeepCompareEffect } from "@react-hookz/web";
import { useState } from "react";
import type { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { Asset } from "../server/digital-asset-service";
import { useAssets } from "./use-assets";

/**
 * Wraps useAssets to preserve the last known data during reloads,
 * preventing UI flicker when reload() nulls out data.
 */
export const useCachedAssets = (): ReturnType<typeof useSupabaseLoader<Asset[]>> => {
  const { data, loading, error, reload } = useAssets();
  const [staleData, setStaleData] = useState<Asset[] | null>(null);

  useDeepCompareEffect(() => {
    if (data) setStaleData(data);
  }, [data]);

  if (loading && !staleData)
    return {
      data: null,
      loading: true,
      error: null,
      reload,
    };

  if (error || (!data && !staleData))
    return {
      data: null,
      loading: false,
      error: error ?? new Error("Failed to load assets"),
      reload,
    };

  return {
    data: data ?? staleData ?? [],
    loading: false,
    error: null,
    reload,
  };
};
